import React from "react"
import { graphql } from "gatsby"
import { Box, Container } from "@theme-ui/components"
import Layout from "../components/layout"
import { getHomePath } from "../utils/path"
import { HelmetDatoCms } from "gatsby-source-datocms"
import LatestArticles from "../components/latestArticles"
import LatestArticlesFairs from "../components/latestArticlesFairs"
import { useFavicon } from "../hooks/useFavicon"
import Intro from "../components/blocks/intro"
import ImageAndText from "../components/blocks/imageAndText"
import ItemCarousel from "../components/blocks/itemCarousel"
import ProductTypeCollection from "../components/blocks/productTypeCollection"
import HomeAnimationSection from "../components/homeAnimationSection"
import NumberInsights from "../components/NumberInsights"

const Home = ({ data: { page, site, articles, fairs, animationImages } }) => {
  console.log(articles, fairs)
  const favicon = useFavicon().site.faviconMetaTags

  const i18nPaths = site.locales.map(locale => {
    return {
      locale: locale,
      value: getHomePath(locale),
    }
  })

  return (
    <Layout locale={page.locale} i18nPaths={i18nPaths} theme="dark">
      <HelmetDatoCms seo={page.seoMetaTags} favicon={favicon}>
        <html lang={page.locale} />
      </HelmetDatoCms>
      {page.content.map(
        block =>
          block.model.apiKey === "home_animation_section" && (
            <Box
              as="section"
              key={block.id}
              id="home_animation_section"
              sx={{
                overflow: "hidden",
                position: "relative",
                zIndex: 99,
              }}
            >
              <HomeAnimationSection
                animationImages={animationImages}
                block={block}
              />
            </Box>
          )
      )}
      {page.content.map(
        block =>
          block.model.apiKey === "number_insights_collection" && (
            <Box
              as="section"
              key={block.id}
              sx={{
                position: "relative",
                zIndex: 99,
              }}
            >
              <NumberInsights block={block} />
            </Box>
          )
      )}
      {page.content.map(block => (
        <Box
          as="section"
          key={block.id}
          sx={{
            position: "relative",
            zIndex: 0,
          }}
        >
          {block.model.apiKey === "intro" && (
            <Container id="intro_home">
              <Intro
                title={block.title}
                body={block.body}
                alternate={block.alternate}
              />
            </Container>
          )}
          {block.model.apiKey === "image_and_text" && (
            <ImageAndText
              label={block.content.label}
              subtitle={block.content.subtitle}
              title={block.content.title}
              body={block.content.body}
              image={block.image}
            />
          )}
          {block.model.apiKey === "item_carousel" && (
            <ItemCarousel items={block.items} />
          )}
          {block.model.apiKey === "product_type_collection" && (
            <ProductTypeCollection
              title={block.title}
              items={block.productTypes}
            />
          )}
        </Box>
      ))}
      {articles.nodes.length > 0 && (
        <Box
          as="section"
          key={"section_articles"}
          sx={{
            position: "relative",
            zIndex: 0,
          }}
        >
          <LatestArticlesFairs articles={fairs.nodes} />
          <LatestArticles articles={articles.nodes} />
        </Box>
      )}
    </Layout>
  )
}

export default Home

export const query = graphql`
  query HomeQuery($id: String!, $locale: String!) {
    animationImages: allFile(
      filter: { dir: { regex: "/homeImages/" } }
      sort: { name: ASC }
    ) {
      edges {
        node {
          id
          publicURL
          dir
        }
      }
    }
    page: datoCmsHomePage(id: { eq: $id }) {
      id
      title
      locale
      seoMetaTags {
        ...GatsbyDatoCmsSeoMetaTags
      }
      content {
        ... on DatoCmsNumberInsightsCollection {
          id
          title
          numbers {
            legend
            float
            suffix
            prefix
            image {
              gatsbyImageData(
                width: 1480
                placeholder: BLURRED
                forceBlurhash: false
              )
            }
          }
          model {
            apiKey
          }
        }
        ... on DatoCmsHighlightsCollection {
          id
          model {
            apiKey
          }
        }
        ... on DatoCmsHomeAnimationSection {
          sections {
            ... on DatoCmsHomeHeader {
              image {
                gatsbyImageData(
                  width: 1480
                  placeholder: BLURRED
                  forceBlurhash: false
                )
              }
              title
              description
              link {
                anchor
                ... on DatoCmsInternalLink {
                  id
                  anchor
                  locale
                  model {
                    apiKey
                  }
                  link {
                    ... on DatoCmsBlogPage {
                      ...BlogDetails
                    }
                    ... on DatoCmsPage {
                      ...PageDetails
                      ...PageTreeParent
                      ...AllSlugLocales
                    }
                    ... on DatoCmsArticle {
                      ...ArticleDetails
                      ...ArticleAllSlugLocales
                    }
                    ... on DatoCmsArticleCategory {
                      ...ArticleCategoryDetails
                      ...ArticleCategoryAllSlugLocales
                    }
                  }
                }
              }
              model {
                apiKey
              }
            }
            ... on DatoCmsHomeAboutUsPart1 {
              content {
                ... on DatoCmsRichContent {
                  ...RichContent
                }
              }
              model {
                apiKey
              }
            }
            ... on DatoCmsHomeAboutUsPart2 {
              id
              aboutUsNumbers {
                legend
                float
                suffix
                prefix
                image {
                  gatsbyImageData(
                    width: 1480
                    placeholder: BLURRED
                    forceBlurhash: false
                  )
                }
              }
              model {
                apiKey
              }
            }
            ... on DatoCmsHomeFocusSection {
              content {
                ... on DatoCmsRichContent {
                  ...RichContent
                }
              }
              model {
                apiKey
              }
            }
          }
          model {
            apiKey
          }
        }
        ... on DatoCmsHomeHeader {
          image {
            gatsbyImageData(
              width: 1480
              placeholder: BLURRED
              forceBlurhash: false
            )
          }
          title
          description
          link {
            anchor
            ... on DatoCmsInternalLink {
              id
              anchor
              locale
              model {
                apiKey
              }
              link {
                ... on DatoCmsBlogPage {
                  ...BlogDetails
                }
                ... on DatoCmsPage {
                  ...PageDetails
                  ...PageTreeParent
                  ...AllSlugLocales
                }
                ... on DatoCmsArticle {
                  ...ArticleDetails
                  ...ArticleAllSlugLocales
                }
                ... on DatoCmsArticleCategory {
                  ...ArticleCategoryDetails
                  ...ArticleCategoryAllSlugLocales
                }
              }
            }
          }
          model {
            apiKey
          }
        }
        ... on DatoCmsIntro {
          title
          alternate
          body {
            value
          }
          model {
            apiKey
          }
        }
        ... on DatoCmsImageAndText {
          id
          content {
            ... on DatoCmsRichContent {
              ...RichContent
            }
          }
          image {
            gatsbyImageData(
              width: 1480
              placeholder: BLURRED
              forceBlurhash: false
            )
          }
          model {
            apiKey
          }
        }
        ... on DatoCmsItemCarousel {
          id
          items {
            ... on DatoCmsRichContent {
              ...RichContent
            }
          }
          model {
            apiKey
          }
        }
        ... on DatoCmsProductTypeCollection {
          id
          title
          productTypes {
            title
            slug
            ...AllTypeSlugLocales
            locale
            images {
              gatsbyImageData(
                width: 1480
                placeholder: BLURRED
                forceBlurhash: false
              )
            }
          }
          model {
            apiKey
          }
        }
      }
    }
    fairs: allDatoCmsArticle(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: {
        slug: { ne: null }
        locale: { eq: $locale }
        category: { originalId: { eq: "102194255" } }
      }
      limit: 6
    ) {
      nodes {
        ...ArticleDetails
        ...ArticleAllSlugLocales
        ...ArticleMeta
      }
    }
    site: datoCmsSite {
      locales
    }
    articles: allDatoCmsArticle(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: {
        slug: { ne: null }
        locale: { eq: $locale }
        category: { originalId: { ne: "102194255" } }
      }
      limit: 6
    ) {
      nodes {
        ...ArticleDetails
        ...ArticleAllSlugLocales
        ...ArticleMeta
      }
    }
    site: datoCmsSite {
      locales
    }
  }
`
